import { useState, FormEvent, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { PublicNavbar } from '../components/layout/PublicNavbar';
import { AuroraBG } from '../components/ui/AuroraBG';
import { GlassPanel } from '../components/ui/GlassPanel';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { ChefHat, Loader2, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';

export function RegisterPage() {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [countryCode, setCountryCode] = useState('EL');
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingVat, setValidatingVat] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [vatValidated, setVatValidated] = useState(false);
  const { signUp, setCurrentTenant } = useAuth();
  const navigate = useNavigate();

  const checkEmailAvailability = useCallback(async (emailToCheck: string) => {
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setEmailAvailable(null);
      return;
    }

    setCheckingEmail(true);
    setEmailAvailable(null);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-email`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailToCheck,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setEmailAvailable(data.available);
      } else {
        setEmailAvailable(null);
      }
    } catch {
      setEmailAvailable(null);
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (email) {
        checkEmailAvailability(email);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [email, checkEmailAvailability]);

  const validateVatWithVIES = async () => {
    if (!vatNumber.trim()) {
      setError('Please enter a VAT number');
      return false;
    }

    setValidatingVat(true);
    setError('');

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-vat`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          countryCode: countryCode,
          vatNumber: vatNumber.trim(),
        }),
      });

      if (!response.ok) {
        setError('VAT validation service unavailable. Please try again.');
        setValidatingVat(false);
        return false;
      }

      const data = await response.json();

      if (data.error) {
        console.error('VAT Validation Error:', data);
        setError(data.error);
        setValidatingVat(false);
        return false;
      }

      if (!data.valid) {
        console.error('Invalid VAT:', data);
        setError('Invalid VAT number. Please verify your VAT registration.');
        setValidatingVat(false);
        return false;
      }

      if (data.name && data.name.trim()) {
        setCompanyName(data.name.trim());
      }
      if (data.address && data.address.trim()) {
        setAddress(data.address.trim());
      }

      setVatValidated(true);
      setValidatingVat(false);
      return true;
    } catch (err) {
      console.error('VAT validation error:', err);
      setError('Unable to validate VAT number. Please try again or contact support.');
      setValidatingVat(false);
      return false;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (emailAvailable === false) {
      setError(t('auth.errors.emailAlreadyUsed'));
      return;
    }

    if (!vatValidated) {
      setError(t('auth.errors.vatValidationRequired', { defaultValue: 'Please validate your VAT number using the "Validate VAT" button before proceeding.' }));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.errors.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth.errors.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      const { error: signUpError } = await signUp(email, password, fullName);

      if (signUpError) {
        if (signUpError.message.includes('already registered') || signUpError.message.includes('User already registered')) {
          setError(t('auth.errors.emailAlreadyUsed'));
        } else {
          setError(signUpError.message);
        }
        setLoading(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const slug = companyName.toLowerCase().replace(/[^a-z0-9]+/g, '-');

        let fullVatNumber = vatNumber.trim().toUpperCase();
        if (countryCode === 'EL' && fullVatNumber && !fullVatNumber.startsWith('EL')) {
          fullVatNumber = 'EL' + fullVatNumber;
        } else if (countryCode === 'CY' && fullVatNumber && !fullVatNumber.startsWith('CY')) {
          fullVatNumber = 'CY' + fullVatNumber;
        }

        const { data: tenant, error: tenantError } = await supabase
          .from('tenants')
          .insert({
            name: companyName,
            slug,
            vat_number: fullVatNumber || null,
            country_code: countryCode,
            address: address || null,
            contact_email: email,
            subscription_tier: 'trial',
            subscription_status: 'active',
          })
          .select()
          .single();

        if (tenantError) {
          console.error('Tenant creation error:', tenantError);

          // Check for duplicate VAT number
          if (tenantError.message.includes('duplicate') && tenantError.message.includes('vat')) {
            setError(t('auth.errors.vatAlreadyUsed'));
          }
          // Check for duplicate email
          else if (tenantError.message.includes('duplicate') && tenantError.message.includes('email')) {
            setError(t('auth.errors.emailAlreadyUsed'));
          }
          // Check for duplicate slug (company name)
          else if (tenantError.message.includes('duplicate') && tenantError.message.includes('slug')) {
            setError(t('auth.errors.companyNameUsed', { defaultValue: 'A company with this name already exists. Please use a different company name.' }));
          }
          // Generic duplicate key error
          else if (tenantError.message.includes('duplicate key') || tenantError.message.includes('unique constraint')) {
            setError(t('auth.errors.emailOrVatUsed'));
          }
          // Other errors
          else {
            setError(t('auth.errors.registrationFailed'));
          }

          setLoading(false);
          return;
        }

        await supabase.from('user_tenants').insert({
          user_id: user.id,
          tenant_id: tenant.id,
          role: 'OWNER',
        });

        setCurrentTenant(tenant.id);
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(t('auth.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuroraBG />
      <PublicNavbar />
      <div className="min-h-screen flex items-center justify-center p-4 pt-20">
        <GlassPanel className="w-full max-w-md p-8">
          <div className="flex items-center justify-center mb-8">
            <ChefHat className="w-12 h-12 text-primary-400 mr-3" />
            <h1 className="text-3xl font-bold text-white">EL-OS</h1>
          </div>

          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Create Account
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Full Name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Doe"
              required
            />

            <div className="relative">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className={`pr-10 ${
                  emailAvailable === false
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : emailAvailable === true
                    ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                    : ''
                }`}
              />
              <div className="absolute right-3 top-9 flex items-center">
                {checkingEmail && (
                  <Loader2 className="w-5 h-5 text-primary-400 animate-spin" />
                )}
                {!checkingEmail && emailAvailable === true && (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                )}
                {!checkingEmail && emailAvailable === false && (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
              </div>
              {!checkingEmail && emailAvailable === false && (
                <p className="mt-1 text-sm text-red-400">
                  This email is already registered. <Link to="/login" className="underline hover:text-red-300">Sign in instead</Link>
                </p>
              )}
              {!checkingEmail && emailAvailable === true && (
                <p className="mt-1 text-sm text-green-400">
                  Email is available
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Country
              </label>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                required
              >
                <option value="EL">Greece (EL)</option>
                <option value="CY">Cyprus (CY)</option>
                <option value="DE">Germany (DE)</option>
                <option value="FR">France (FR)</option>
                <option value="IT">Italy (IT)</option>
                <option value="ES">Spain (ES)</option>
                <option value="NL">Netherlands (NL)</option>
                <option value="BE">Belgium (BE)</option>
                <option value="AT">Austria (AT)</option>
                <option value="PT">Portugal (PT)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                VAT Number {countryCode === 'EL' || countryCode === 'CY' ? `(${countryCode} prefix will be added automatically)` : ''}
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  placeholder={countryCode === 'EL' ? '999999999' : countryCode === 'CY' ? '99999999L' : 'VAT Number'}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={validateVatWithVIES}
                  disabled={validatingVat || !vatNumber.trim()}
                  variant="secondary"
                  className="whitespace-nowrap"
                >
                  {validatingVat ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    'Validate VAT'
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/50 mt-1">
                Click "Validate VAT" to auto-fill company details from EU VIES
              </p>
            </div>

            <Input
              label="Company Name"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Your Restaurant"
              required
            />

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Address (Optional)
              </label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Company address"
                rows={2}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
              />
            </div>

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-white/60 hover:text-white/90 transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="relative">
              <Input
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className={`pr-10 ${
                  confirmPassword && password !== confirmPassword
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : confirmPassword && password === confirmPassword
                    ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                    : ''
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-9 text-white/60 hover:text-white/90 transition-colors"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-sm text-red-400">
                  Passwords do not match
                </p>
              )}
              {confirmPassword && password === confirmPassword && (
                <p className="mt-1 text-sm text-green-400">
                  Passwords match
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-error-500/20 border border-error-500/50 text-error-200 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={
                loading ||
                emailAvailable === false ||
                checkingEmail ||
                password !== confirmPassword ||
                !password ||
                !confirmPassword ||
                !vatValidated
              }
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>

            {!vatValidated && (
              <p className="text-xs text-warning-400 text-center mt-2">
                You must validate your VAT number before creating an account
              </p>
            )}
          </form>

          <p className="mt-6 text-center text-white/60 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium">
              Sign in
            </Link>
          </p>
        </GlassPanel>
      </div>
    </>
  );
}
